function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Events, triggerEvent } from '../utils/events';
import { buildWaiter } from '@ember/test-waiters';
let onDragMoveWaiter = buildWaiter('fly-drag-drop:on-drag-move');
const DEFAULT_OPTIONS = {
  classes: ['fly-drag-drop-clone'],
  appendContainer: null,
  cloneElement: null,
  scaleWithTransform: false,
  initialOffset: null,
  transparentizeOriginal: true,
  originalTransparency: 0.5
};
function clone(element) {
  let cloneElement = element.cloneNode(true);

  // Cloning doesn't copy the currently selected option. This iterates through
  // selects and makes sure the right options are selected.
  let originalSelectElements = element.querySelectorAll('select');
  let cloneSelectElements = cloneElement.querySelectorAll('select');
  originalSelectElements.forEach((element, index) => {
    cloneSelectElements[index].selectedIndex = element.selectedIndex;
  });
  return cloneElement;
}
export default class DraggableClone {
  constructor(modifierInstance) {
    _defineProperty(this, "element", void 0);
    _defineProperty(this, "modifierInstance", void 0);
    _defineProperty(this, "cloneSourceElement", null);
    _defineProperty(this, "cloneElement", null);
    _defineProperty(this, "initialOffset", {
      x: 0,
      y: 0
    });
    _defineProperty(this, "scale", 1);
    _defineProperty(this, "dimensionOffsets", {
      x: 0,
      y: 0
    });
    this.element = modifierInstance.draggableElement;
    this.modifierInstance = modifierInstance;
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.endDrag = this.endDrag.bind(this);
  }
  get options() {
    let options = this.modifierInstance.named.pluginOptions?.clone ?? {
      classes: []
    };
    let classes = [...DEFAULT_OPTIONS.classes, ...(options.classes || [])];
    return Object.assign({}, DEFAULT_OPTIONS, options, {
      classes
    });
  }
  attach() {
    this.element.addEventListener(Events.DragStart, this.onDragStart);
    this.element.addEventListener(Events.DragMove, this.onDragMove);
    this.element.addEventListener(Events.DragEnd, this.onDragEnd);
  }
  detach() {
    this.element.removeEventListener(Events.DragStart, this.onDragStart);
    this.element.removeEventListener(Events.DragMove, this.onDragMove);
    this.element.removeEventListener(Events.DragEnd, this.onDragEnd);
    this.endDrag();
  }
  onDragStart(event) {
    let {
      options
    } = this;
    if (options.transparentizeOriginal) {
      this.element.style.opacity = `${options.originalTransparency}`;
    }
    this.cloneSourceElement = this.getCloneSource();
    this.cloneElement = clone(this.cloneSourceElement);
    this.cloneElement.classList.add(...options.classes);
    this.cloneElement.dataset.flyDraggableClone = 'true';
    if (this.modifierInstance.named.handle) {
      let handleElement = this.cloneElement.querySelector(`.${this.modifierInstance.named.handle}`);
      if (!handleElement) {
        return;
      }
      handleElement.style.opacity = '1';
      handleElement.style.visibility = 'visible';
    }
    this.positionClone(event, true);
    let appendContainer = this.getAppendContainer();
    if (!this.getAppendContainer) {
      return;
    }
    appendContainer?.appendChild(this.cloneElement);
    triggerEvent(this.element, Events.CloneCreate, {
      clientX: event.detail.clientX,
      clientY: event.detail.clientY,
      clone: this.cloneElement
    });
  }
  onDragMove(event) {
    let token = onDragMoveWaiter.beginAsync();
    requestAnimationFrame(() => {
      this.positionClone(event);
      onDragMoveWaiter.endAsync(token);
    });
  }
  onDragEnd(event) {
    this.endDrag(event);
  }
  endDrag(event) {
    this.element.style.removeProperty('opacity');
    if (this.cloneElement) {
      this.cloneElement.remove();
      this.cloneElement = null;
      triggerEvent(this.element, Events.CloneDestroy, {
        clientX: event?.detail.clientX ?? 0,
        clientY: event?.detail.clientY ?? 0
      });
    }
  }
  getCloneSource() {
    let {
      cloneElement
    } = this.options;
    if (typeof cloneElement === 'string') {
      return document.querySelector(cloneElement);
    } else if (cloneElement instanceof HTMLElement) {
      return cloneElement;
    } else if (typeof cloneElement === 'function') {
      return cloneElement(this.element);
    } else {
      return this.element;
    }
  }
  getAppendContainer() {
    let {
      appendContainer
    } = this.options;
    if (typeof appendContainer === 'string') {
      return document.querySelector(appendContainer);
    } else if (appendContainer instanceof HTMLElement) {
      return appendContainer;
    } else if (typeof appendContainer === 'function') {
      return appendContainer(this.element);
    } else {
      return this.element.parentNode;
    }
  }
  getInitialOffset(element, event) {
    let {
      initialOffset
    } = this.options;
    if (typeof initialOffset === 'function') {
      return initialOffset(element, event);
    }
    let elementCoordinates = element.getBoundingClientRect();
    let elementStyles = getComputedStyle(element);
    let elementMargin = {
      top: parseInt(elementStyles.getPropertyValue('margin-top'), 10),
      left: parseInt(elementStyles.getPropertyValue('margin-left'), 10)
    };

    // If the draggable element is hidden, the size will be 0, and we can't divide by that.
    let draggableXScale = elementCoordinates.width === 0 ? 1 : ((event.detail.clientX ?? 0) - elementCoordinates.left + elementMargin.left) / elementCoordinates.width;
    let draggableYScale = elementCoordinates.height === 0 ? 1 : ((event.detail.clientY ?? 0) - elementCoordinates.top + elementMargin.top) / elementCoordinates.height;
    return {
      x: draggableXScale * elementCoordinates.width,
      y: draggableYScale * elementCoordinates.height
    };
  }
  positionClone(event, initial = false) {
    let {
      scaleWithTransform
    } = this.options;
    let cloneElement = this.cloneElement;
    if (!cloneElement) {
      return;
    }
    if (initial) {
      let cloneSourceElement = this.cloneSourceElement;
      if (!cloneSourceElement) {
        return;
      }
      let cloneSourceElementCoordinates = cloneSourceElement.getBoundingClientRect();
      if (scaleWithTransform) {
        this.scale = cloneSourceElementCoordinates.width / cloneSourceElement.offsetWidth;
        if (this.scale !== 1) {
          // If we chose to scale the mirror node to the same size as the
          // original node, we'll also need to apply extra X and Y transitions.
          // This is because the scale transform's origin is in the center of
          // the mirror, so we'll need to translate further by half the
          // difference in width/height between the scaled and non-scaled
          // versions.
          let sizeScale = 1 - 1 / this.scale;
          this.dimensionOffsets = {
            x: cloneSourceElementCoordinates.width * sizeScale / 2,
            y: cloneSourceElementCoordinates.height * sizeScale / 2
          };
        }
      }
      cloneElement.style.width = `${cloneSourceElementCoordinates.width / this.scale}px`;
      cloneElement.style.height = `${cloneSourceElementCoordinates.height / this.scale}px`;
      this.initialOffset = this.getInitialOffset(cloneSourceElement, event);
    }
    let x = (event.detail.clientX ?? 0) - this.initialOffset.x;
    let y = (event.detail.clientY ?? 0) - this.initialOffset.y;
    if (scaleWithTransform && this.dimensionOffsets) {
      x = x + this.dimensionOffsets.x;
      y = y + this.dimensionOffsets.y;
    }
    cloneElement.style.transform = `translate3d(${x}px, ${y}px, 0) ${this.scale !== 1 ? `scale(${this.scale})` : ''}`;
  }
}